/**
 * <collectors-privilege>
 * Animated Collector's Privilege and Free Shipping progress widget.
 */
(function () {
  if (customElements.get('collectors-privilege')) return;

  const CHECK_SVG =
    '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.1 6.7L5 9.5L10.9 3.4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const GIFT_SVG =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 12v9H4v-9M2 7h20v5H2V7zm10 14V7m0 0C12 4 9 2 7.5 3.5S9 7 12 7zm0 0c0-3 3-5 4.5-3.5S15 7 12 7z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const TRUCK_SVG =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 6h4l3 4v6h-2M14 6v10M14 6H3v10h2m2 0a2 2 0 104 0m6 0a2 2 0 104 0M9 16h6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class CollectorsPrivilege extends HTMLElement {
    connectedCallback() {
      this.compact = this.hasAttribute('compact');
      this._previousUnlockedCount = null;
      this._lastRewardState = null;
      this._lastShippingState = null;
      this._unlockTimer = null;
      this._unsubscribeReward = window.RewardManager.on('reward:updated', (state) => this.render(state));
      this._unsubscribeShipping = window.ShippingManager.on('shipping:updated', (state) => this.updateShipping(state));

      this.draw(window.RewardManager.calculate(0), { initial: true });
      window.RewardManager.getCurrentState().then((state) => this.render(state));
    }

    disconnectedCallback() {
      this._unsubscribeReward?.();
      this._unsubscribeShipping?.();
      clearTimeout(this._unlockTimer);
    }

    render(state) {
      const previousUnlocked = this._previousUnlockedCount;
      const unlockedCount = state.unlockedTiers.length;
      const hasNewUnlock = previousUnlocked !== null && unlockedCount > previousUnlocked;

      if (hasNewUnlock) {
        const celebrationState = { ...state, progressPercent: 100, celebratingIndex: unlockedCount - 1 };
        this.draw(celebrationState, { celebrating: true });
        window.LuxuryAnimations?.confetti(this.querySelector('[data-cp-widget]'));

        clearTimeout(this._unlockTimer);
        this._unlockTimer = setTimeout(() => {
          this.draw(state);
          this._previousUnlockedCount = unlockedCount;
        }, state.isMaxed ? 0 : 760);
      } else {
        this.draw(state);
        this._previousUnlockedCount = unlockedCount;
      }
    }

    draw(state, { initial = false, celebrating = false } = {}) {
      const fmt = (cents) => window.RewardManager.formatMoney(cents);
      const widgetClass = this.compact ? 'cp-widget cp-widget--compact' : 'cp-widget';
      const progressValue = Math.round(state.progressPercent);
      const previousProgressValue = this._lastRewardState ? Math.round(this._lastRewardState.progressPercent) : 0;
      const shipping = this._lastShippingState || window.ShippingManager.calculate(state.subtotalCents);
      const shippingProgress = Math.round(shipping.progressPercent);
      const previousShippingProgress = this._lastShippingState
        ? Math.round(this._lastShippingState.progressPercent)
        : 0;
      const startProgress = initial ? progressValue : previousProgressValue;
      const startShippingProgress = initial ? shippingProgress : previousShippingProgress;

      this.innerHTML = `
        <div class="${widgetClass} lux-scope" data-cp-widget>
          <div class="cp-widget__header">
            <div>
              <p class="cp-widget__title">The Collector's Privilege</p>
              <p class="cp-widget__subtitle">Exclusive rewards unlock automatically as your cart grows.</p>
            </div>
            <span class="cp-widget__badge">${state.isMaxed ? 'Elite' : 'Live'}</span>
          </div>

          <div class="cp-progress-card${state.isMaxed ? ' is-complete' : ''}${state.unlockedTiers.length ? ' is-unlocked' : ''}${celebrating ? ' is-celebrating' : ''}">
            <div class="cp-progress-card__copy">
              <p class="cp-progress-card__headline">${state.headline}</p>
              <p class="cp-progress-card__unlocked">${state.unlockedText}</p>
              <p class="cp-progress-card__message">${state.message}</p>
            </div>
            <div class="cp-progress-row">
              <div class="cp-progress" role="progressbar" aria-valuenow="${progressValue}" aria-valuemin="0" aria-valuemax="100" aria-label="Collector's Privilege progress">
                <div class="cp-progress__fill" data-cp-fill style="width: ${startProgress}%;"></div>
              </div>
              <span class="cp-progress__percent" data-cp-percent>${startProgress}%</span>
            </div>
          </div>

          <div class="cp-tiers" role="list">
            ${this.renderTiers(state.tiers, fmt, state.celebratingIndex)}
          </div>

          <div class="cp-shipping${shipping.unlocked ? ' is-complete' : ''}" data-cp-shipping>
            ${this.renderShipping(shipping, startShippingProgress)}
          </div>

          ${
            state.unlockedTiers.length > 0
              ? `<div class="cp-membership">
                  <span class="cp-membership__icon">${GIFT_SVG}</span>
                  <div>
                    <p class="cp-membership__title">Your Collector Benefits</p>
                    <ul class="cp-membership__list">
                      <li>Rewards tracked automatically</li>
                      <li>No coupon hunt at checkout</li>
                      <li>Eligible offers stay synchronized</li>
                    </ul>
                  </div>
                </div>`
              : ''
          }
        </div>
      `;

      window.LuxuryCartUtils?.nextFrame(() => {
        const fill = this.querySelector('[data-cp-fill]');
        if (fill) fill.style.width = `${progressValue}%`;
        this.animatePercent('[data-cp-percent]', startProgress, progressValue);
        this.animateShippingProgress(startShippingProgress, shippingProgress);
      });

      if (!initial && celebrating) {
        window.LuxuryAnimations?.pulse(this.querySelector('.cp-progress-card'), 'cp-unlock-pulse');
      }

      this._lastRewardState = state;
      this._lastShippingState = shipping;
    }

    renderTiers(tiers, fmt, celebratingIndex) {
      return tiers
        .map((tier, index) => {
          const status = tier.status;
          const label = status === 'completed' ? 'Completed' : status === 'current' ? 'Active tier' : 'Next';
          const icon = status === 'completed' ? `<span class="cp-tier__check">${CHECK_SVG}</span>` : '';
          const classes = [
            'cp-tier',
            `cp-tier--${status}`,
            celebratingIndex === index ? 'cp-tier--celebrating' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return `
            <div class="${classes}" role="listitem">
              <div class="cp-tier__top">
                ${icon}
                <span class="cp-tier__label">${label}</span>
              </div>
              <div class="cp-tier__amount">${fmt(tier.threshold)}+</div>
              <div class="cp-tier__reward">${fmt(tier.reward)} Reward</div>
            </div>`;
        })
        .join('');
    }

    renderShipping(shipping, visibleProgress = Math.round(shipping.progressPercent)) {
      const fmt = (cents) => window.RewardManager.formatMoney(cents);
      const progress = Math.round(shipping.progressPercent);

      return `
        <div class="cp-shipping__header">
          <span class="cp-shipping__icon">${TRUCK_SVG}</span>
          <div>
            <p class="cp-shipping__title">Free Shipping</p>
            <p class="cp-shipping__target">${fmt(shipping.threshold)} threshold</p>
          </div>
          <span class="cp-shipping__percent" data-cp-shipping-percent>${visibleProgress}%</span>
        </div>
        <div class="cp-shipping__track" role="progressbar" aria-valuenow="${progress}" aria-valuemin="0" aria-valuemax="100" aria-label="Free shipping progress">
          <span class="cp-shipping__fill" data-cp-shipping-fill style="width: ${visibleProgress}%;"></span>
        </div>
        <p class="cp-shipping__message">${shipping.message}</p>
      `;
    }

    updateShipping(shipping) {
      const node = this.querySelector('[data-cp-shipping]');
      if (!node) return;
      const previousProgress = this._lastShippingState ? Math.round(this._lastShippingState.progressPercent) : 0;
      const nextProgress = Math.round(shipping.progressPercent);
      node.classList.toggle('is-complete', shipping.unlocked);
      node.innerHTML = this.renderShipping(shipping, previousProgress);
      window.LuxuryCartUtils?.nextFrame(() => this.animateShippingProgress(previousProgress, nextProgress));
      this._lastShippingState = shipping;
    }

    animatePercent(selector, from, to) {
      const element = this.querySelector(selector);
      if (!element) return;

      if (!window.LuxuryAnimations || from === to) {
        element.textContent = `${to}%`;
        return;
      }

      window.LuxuryAnimations.animateNumber({
        from,
        to,
        duration: 620,
        onUpdate: (value) => {
          element.textContent = `${Math.round(value)}%`;
        },
      });
    }

    animateShippingProgress(from, to) {
      const fill = this.querySelector('[data-cp-shipping-fill]');
      if (fill) fill.style.width = `${to}%`;
      this.animatePercent('[data-cp-shipping-percent]', from, to);
    }
  }

  customElements.define('collectors-privilege', CollectorsPrivilege);
})();
